<template>
  <div v-if="pageLoading" class="m-3">
    <placeholder-content :is-load="true" />
  </div>
  <div v-else-if="!hasPermission" class="m-3">
    <access-denied />
  </div>
  <div v-else-if="featureUnavailable" class="m-3">
    <feature-not-available back-route="reports" back-text="_plan.Back to reports" />
  </div>
  <div v-else class="p-3">
    <!-- Header -->
    <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2">
      <div class="d-flex align-items-center gap-2">
        <div>
          <h5 class="fw-bold mb-0">
            <i class="fa-solid fa-clock me-2" style="color: #06b6d4;"></i>{{ $t('Sales by Hour') }}
          </h5>
          <small class="text-muted">{{ $t('Sales') }}</small>
        </div>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-primary btn-sm" @click="downloadPdf" :disabled="pdfLoading || loading">
          <i class="fa-solid fa-download me-1"></i>{{ $t('Download') }}
        </button>
        <button class="btn btn-primary btn-sm" @click="previewPdf" :disabled="pdfLoading || loading">
          <span v-if="pdfLoading"><i class="fa-solid fa-spinner fa-spin me-1"></i>{{ $t('Generating') }}...</span>
          <span v-else><i class="fa-solid fa-file-pdf me-1"></i>{{ $t('Preview PDF') }}</span>
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-body py-2 px-3">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <select v-model="filters.branch" class="form-select form-select-sm" style="min-width: 140px; max-width: 200px;" :disabled="loading">
            <option value="all">{{ $t('All Branches') }}</option>
            <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
          </select>
          <div class="input-group input-group-sm w-auto date-range-input-group">
            <span class="input-group-text"><i class="fa-regular fa-calendar"></i></span>
            <date-range-picker
              v-model="filters.date_range"
              :date-range="filters.date_range"
              :locale-data="dateRangeLocale"
              :ranges="dateRanges"
              :opens="'right'"
              :auto-apply="true"
              :show-dropdowns="true"
              :control-container-class="''"
            >
              <template v-slot:input="picker">
                <span v-if="picker.startDate && picker.endDate">
                  {{ formatDate(picker.startDate) }} — {{ formatDate(picker.endDate) }}
                </span>
                <span v-else class="text-muted">{{ $t('Select date range') }}</span>
              </template>
            </date-range-picker>
          </div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-if="errorMessage" class="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
      <span><i class="fa-solid fa-triangle-exclamation me-2"></i>{{ errorMessage }}</span>
      <button class="btn btn-sm btn-outline-danger" @click="loadReport">{{ $t('Retry') }}</button>
    </div>

    <div :class="{ 'report-loading': loading }">

    <!-- Hourly Chart -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-header bg-white border-0 py-3">
        <h6 class="mb-0 fw-semibold"><i class="fa-solid fa-clock me-2 text-info"></i>{{ $t('Sales by Hour') }}</h6>
      </div>
      <div class="card-body pt-0">
        <apexchart ref="hourlyChart" type="bar" height="350" :options="hourlyChartOptions" :series="hourlyChartSeries" />
      </div>
    </div>

    <!-- Hourly Breakdown Table -->
    <div class="card border-0 shadow-sm mb-4">
      <div class="card-header bg-white border-0 py-3">
        <h6 class="mb-0 fw-semibold"><i class="fa-solid fa-table me-2 text-secondary"></i>{{ $t('Hourly Breakdown') }}</h6>
      </div>
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover table-sm mb-0 align-middle">
            <thead class="table-light">
              <tr>
                <th class="ps-3">{{ $t('Time') }}</th>
                <th class="text-end">{{ $t('Orders') }}</th>
                <th class="text-end">{{ $t('Revenue') }}</th>
                <th class="text-end pe-3">{{ $t('Avg. per Order') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!hourlyData.length && !loading">
                <td colspan="4" class="text-center text-muted py-4">{{ $t('No data') }}</td>
              </tr>
              <tr v-for="h in hourlyData" :key="h.label">
                <td class="ps-3">{{ h.label }}</td>
                <td class="text-end">{{ h.orders }}</td>
                <td class="text-end fw-semibold">{{ formatCurrency(h.revenue) }}</td>
                <td class="text-end pe-3">{{ formatCurrency(h.avg) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    </div><!-- /loading wrapper -->

    <!-- Loading spinner -->
    <div v-if="loading" class="report-loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ $t('Loading') + '...' }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue3-apexcharts';
import { Reports } from '@/services/endpoint/reports.service';
import reportMixin from './reportMixin';

export default {
  name: 'ReportSalesByHour',
  components: { apexchart: VueApexCharts },
  mixins: [reportMixin],
  data() {
    return {
      reportLabel: 'Sales by Hour',
      hourly: [],
    };
  },
  methods: {
    async fetchReport() {
      const res = await Reports.salesByHour(this.buildFetchParams());
      const payload = res?.data?.data || {};
      const filter  = res?.data?.filter || {};
      this.branches = filter.branches || [];
      this.hourly   = payload.hourly  || [];
    },

    async buildPdfDefinition() {
      await this.$nextTick();
      const chartImg = await this.getChartImage('hourlyChart');
      const content = [
        ...this.buildPdfHeader(),
        { text: this.$t('Sales by Hour'), style: 'sectionTitle' },
      ];
      if (chartImg) {
        content.push({ image: chartImg, width: 500, margin: [0, 0, 0, 12] });
      }
      content.push(
        { text: this.$t('Hourly Breakdown'), style: 'sectionTitle' },
        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: this.$t('Time'), style: 'tableHeader' },
                { text: this.$t('Orders'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Revenue'), style: 'tableHeader', alignment: 'right' },
                { text: this.$t('Avg. per Order'), style: 'tableHeader', alignment: 'right' },
              ],
              ...this.hourlyData.map(h => [
                { text: h.label, style: 'tableCell' },
                { text: String(h.orders), style: 'tableCell', alignment: 'right' },
                { text: this.formatCurrencyRaw(h.revenue), style: 'tableCell', alignment: 'right' },
                { text: this.formatCurrencyRaw(h.avg), style: 'tableCell', alignment: 'right' },
              ]),
            ],
          },
          layout: 'lightHorizontalLines',
        },
      );
      return { content };
    },
  },
  computed: {
    hourlyData() {
      return (this.hourly || []).map(h => ({
        label: h.label || (h.hour_start != null ? `${String(h.hour_start).padStart(2, '0')}:00-${String((h.hour_start + 1) % 24).padStart(2, '0')}:00` : '-'),
        orders: h.orders || 0,
        revenue: h.revenue || 0,
        avg: h.avg || (h.orders > 0 ? (h.revenue || 0) / h.orders : 0),
      }));
    },
    hourlyChartSeries() {
      return [{ name: this.$t('Revenue'), data: this.hourlyData.map(h => h.revenue) }];
    },
    hourlyChartOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: 'Sarabun, sans-serif' },
        xaxis: { categories: this.hourlyData.map(h => h.label) },
        yaxis: { labels: { formatter: v => this.formatCompact(v) } },
        colors: ['#06b6d4'],
        plotOptions: { bar: { borderRadius: 4, columnWidth: '60%' } },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: v => this.formatCurrency(v) } },
        grid: { borderColor: '#f1f5f9' },
      };
    },
  },
};
</script>

<style scoped>
.report-loading {
  opacity: 0.5;
  pointer-events: none;
  transition: opacity 0.2s;
}
.report-loading-overlay {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1050;
}
</style>
